import { useState, useEffect, useRef,useCallback } from "react";
import {
  Edit2,
  Trash2,
  Plus,
  LogOut,
  Play,
  Image,
  ExternalLink,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";

import constantObj from "../constant/constant";

// Assuming constantObj.API_URL_DEV is set
const API_BASE_URL = constantObj?.API_URL_DEV;

/**
 * Convert Windows backslashes to forward slashes
 * @param {string} path - File path with backslashes
 * @returns {string} - Path with forward slashes
 */
export const fixFilePath = (path) => {
  if (!path || typeof path !== "string") return "";
  return path.replace(/\\/g, "/");
};

const apiCall = async (endpoint, options = {}) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
    ...options.headers,
  };

  // Do NOT set Content-Type if body is FormData (required for file uploads)
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  } else {
    // Ensure FormData does not use Content-Type: application/json
    delete headers["Content-Type"];
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // console.log(response);
  if (!response.ok) {
    // Attempt to read error message from body if available
    const errorBody = await response
      .json()
      .catch(() => ({ message: "API request failed" }));
    console.log(errorBody, errorBody.message);
    throw new Error(errorBody.message || "API request failed");
  }

  return response.json();
};

const Alert = ({ type, message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === "success" ? "bg-emerald-950" : "bg-red-950";
  const borderColor =
    type === "success" ? "border-emerald-800" : "border-red-800";
  const textColor = type === "success" ? "text-emerald-200" : "text-red-200";
  const IconComponent = type === "success" ? CheckCircle : AlertCircle;

  return (
    <div
      className={`fixed top-4 right-4 flex items-center gap-3 px-4 py-3 rounded-lg border ${bgColor} ${borderColor} ${textColor} z-50 shadow-xl`}
    >
      <IconComponent size={20} />
      <span className="text-sm font-medium">{message}</span>
    </div>
  );
};

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700">
        <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-900 rounded-t-2xl">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// NOTE: You must ensure 'Modal', 'ProofCard', and 'getMediaUrl'
// are imported or passed as props from the parent component.

// ----------------------------------------------------
// Component to handle the live preview display (Define this in the same file)
// ----------------------------------------------------
const ProofCardPreview = ({ proofData, allMedia, ProofCard, getMediaUrl }) => {
  // 1. Map allMedia into the structure ProofCard expects
  const mediaForCard = allMedia.map((item, index) => ({
    // Use properties from the item or fallback values
    type:
      item.type || (item.name?.endsWith(".mp4") ? "video/mp4" : "image/jpeg"),
    path: item.path || "", // Existing path or empty for new files
    level: proofData.levels ? proofData.levels[index] : "",
    isNew: item.isNew,
    name: item.name,
  }));

  // 2. Create a virtual 'proof' object
  const virtualProof = {
    ...proofData.formData,
    media: mediaForCard,
  };

  // 3. Render the ProofCard
  return (
    <div className="sticky top-4 border border-blue-500/50 rounded-xl p-4 bg-slate-800/50 h-fit">
      <h4 className="text-lg font-semibold text-blue-300 mb-3">
        Live Proof Preview
      </h4>
      {ProofCard ? (
        <ProofCard proof={virtualProof} onEdit={() => {}} onDelete={() => {}} />
      ) : (
        <p className="text-red-400">
          ProofCard component not found for preview.
        </p>
      )}
    </div>
  );
};
// ----------------------------------------------------

const ProofFormModal = ({
  isOpen,
  onClose,
  proof,
  onSubmit,
  isLoading,
  ProofCard,
  getMediaUrl,
}) => {
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    type: "",
    description: "",
    category: "",
    orientation: "",
  });

  const [newFiles, setNewFiles] = useState([]);
  const [allMedia, setAllMedia] = useState([]);
  const [levels, setLevels] = useState([]);

  useEffect(() => {
    if (proof) {
      setFormData({
        type: proof.type || "",
        description: proof.description || "",
        category: proof.category || "",
        orientation: proof.orientation || "",
      });
      setAllMedia(proof.media || []);
      setLevels(proof.media?.map((m) => m.level || "") || []);
      setNewFiles([]);
    } else {
      setFormData({
        type: "",
        description: "",
        category: "",
        orientation: "",
      });
      setAllMedia([]);
      setLevels([]);
      setNewFiles([]);
    }
  }, [proof, isOpen]);

  // 🟢 FUNCTION FIX 1: handleFilesChange
  const handleFilesChange = (e) => {
    const newlySelectedFiles = Array.from(e.target.files || []);

    if (newlySelectedFiles.length === 0) return;

    setNewFiles((prevNewFiles) => [...prevNewFiles, ...newlySelectedFiles]);

    const tempNewMedia = newlySelectedFiles.map((file) => ({
      name: file.name,
      type: file.type,
      isNew: true,
    }));

    setAllMedia((prevAllMedia) => [...prevAllMedia, ...tempNewMedia]);
    setLevels((prevLevels) => [
      ...prevLevels,
      ...newlySelectedFiles.map(() => ""),
    ]);

    e.target.value = null;
  };

  // 🟢 FUNCTION FIX 2: handleRemoveMedia
  const handleRemoveMedia = (index, isNew) => {
    const updatedAllMedia = allMedia.filter((_, i) => i !== index);
    setAllMedia(updatedAllMedia);
    setLevels(levels.filter((_, i) => i !== index));

    if (isNew) {
      setNewFiles((prev) => {
        let countNewBeforeIndex = 0;
        for (let i = 0; i < index; i++) {
          if (allMedia[i].isNew) {
            countNewBeforeIndex++;
          }
        }
        // The correct index to remove from newFiles is the count of new files before the removal index
        return prev.filter((_, i) => i !== countNewBeforeIndex);
      });
    }
  };

  // 🟢 FUNCTION FIX 3: updateLevel
  const updateLevel = (index, value) => {
    const newLevels = [...levels];
    newLevels[index] = value;
    setLevels(newLevels);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append("type", formData.type);
    data.append("description", formData.description);
    data.append("category", formData.category);
    data.append("orientation", formData.orientation);
    // 🟢 SERVER FIX: Use "media" field name (to match your Multer config)
    newFiles.forEach((file) => {
      data.append("media", file);
    });
    data.append("levels", JSON.stringify(levels));

    // 🟢 DEBUGGING: Log FormData contents properly
    console.log("--- FormData Contents for Submission ---");
    let hasData = false;
    for (const pair of data.entries()) {
      hasData = true;
      if (pair[1] instanceof File) {
        console.log(
          pair[0],
          ":",
          "File:",
          pair[1].name,
          `(${Math.round(pair[1].size / 1024)} KB)`,
        );
      } else {
        console.log(pair[0], ":", pair[1]);
      }
    }
    if (!hasData) {
      console.log("WARNING: FormData is empty! Check state logic.");
    }
    console.log("--------------------------------------");

    onSubmit(data, proof?._id);
  };

  const filesToDisplay = allMedia;
  const previewData = { formData, levels };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={proof ? "Edit Proof" : "Add New Proof"}
    >
      <div className="grid md:grid-cols-2 gap-8">
        {/* Column 1: Form Inputs and Controls */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Type Input */}
          <div>
            <label className="block text-sm font-semibold text-blue-400 mb-2">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-100 transition-all"
              required
            >
              <option value="">Select type</option>
              <option value="images">Images</option>
              <option value="video">Video</option>
              <option value="media">Media</option>
              <option value="mixed">Mixed</option>
            </select>
          </div>

          {/* Category Input */}
          <div>
            <label className="block text-sm font-semibold text-blue-400 mb-2">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-100 transition-all"
              required
            >
              <option value="">Select category</option>
              <option value="Live">Live</option>
              <option value="Prom">Prom</option>
            </select>
          </div>

          {/* Orientation Input */}
          <div>
            <label className="block text-sm font-semibold text-blue-400 mb-2">
              Orientation
            </label>
            <select
              value={formData.orientation}
              onChange={(e) =>
                setFormData({ ...formData, orientation: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-100 transition-all"
            >
              <option value="">Select orientation</option>
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
              <option value="square">Square</option>
            </select>
          </div>

          {/* Description Input */}
          <div>
            <label className="block text-sm font-semibold text-blue-400 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-100 resize-none transition-all"
              rows="4"
              required
              placeholder="Enter proof description..."
            />
          </div>

          {/* MEDIA UPLOAD SECTION */}
          <div>
            <label className="block text-sm font-semibold text-blue-400 mb-2">
              Media Files ({filesToDisplay.length} total)
            </label>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFilesChange}
              className="hidden"
              accept="image/*,video/*"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current.click()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-medium shadow-md shadow-blue-600/20"
            >
              <Plus size={18} /> Add Media Files
            </button>
            <p className="text-sm text-slate-400 mt-2">
              {proof && filesToDisplay.length > 0
                ? `Total media files: ${filesToDisplay.length}. ${newFiles.length} new file(s) added.`
                : `Start by adding image or video files.`}
            </p>
          </div>

          {/* Level inputs for each file */}
          {filesToDisplay.length > 0 && (
            <div className="bg-slate-900 p-4 rounded-lg space-y-4 max-h-64 overflow-y-auto border border-slate-700">
              <p className="text-sm text-slate-300 font-medium sticky top-0 bg-slate-900 pb-2 border-b border-slate-700">
                Set optional labels for each media item
              </p>
              {filesToDisplay.map((media, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="flex-1">
                    <p
                      className={`text-xs mb-1 font-medium truncate ${media.isNew ? "text-green-400" : "text-slate-400"}`}
                    >
                      {media.isNew
                        ? `(NEW) ${media.name}`
                        : `(Existing) ${media.path.split("/").pop()}`}
                    </p>
                    <input
                      type="text"
                      value={levels[index] || ""}
                      onChange={(e) => updateLevel(index, e.target.value)}
                      placeholder={`e.g., Phase ${index + 1} Passed (Optional Level)`}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-100 placeholder-slate-500 text-sm transition-all"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveMedia(index, media.isNew)}
                    className="p-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                    title={`Remove ${media.isNew ? "new" : "existing"} file`}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-700 rounded-lg hover:bg-slate-800 transition-all text-slate-300 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-all flex items-center gap-2 font-medium"
            >
              {isLoading && <Loader2 size={16} className="animate-spin" />}
              {proof ? "Update" : "Add"}
            </button>
          </div>
        </form>

        {/* Column 2: Live Preview */}
        <div className="hidden md:block">
          <ProofCardPreview
            proofData={previewData}
            allMedia={allMedia}
            getMediaUrl={getMediaUrl}
            ProofCard={ProofCard}
          />
        </div>
      </div>
    </Modal>
  );
};

const SocialFormModal = ({ isOpen, onClose, social, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    label: "",
    url: "",
    status: "active",
  });

  useEffect(() => {
    if (social) {
      setFormData({
        label: social.label || "",
        url: social.url || "",
        status: social.status || "active",
      });
    } else {
      setFormData({
        label: "",
        url: "",
        status: "active",
      });
    }
  }, [social, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData, social?._id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={social ? "Edit Social Media" : "Add Social Media"}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-blue-400 mb-2">
            Label
          </label>
          <input
            type="text"
            placeholder="e.g., Facebook, Instagram"
            value={formData.label}
            onChange={(e) =>
              setFormData({ ...formData, label: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-100 placeholder-slate-500 transition-all"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-blue-400 mb-2">
            URL
          </label>
          <input
            type="url"
            placeholder="https://..."
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-100 placeholder-slate-500 transition-all"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-blue-400 mb-2">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-100 transition-all"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="flex gap-3 justify-end pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-700 rounded-lg hover:bg-slate-800 transition-all text-slate-300 font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-all flex items-center gap-2 font-medium"
          >
            {isLoading && <Loader2 size={16} className="animate-spin" />}
            {social ? "Update" : "Add"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * Build complete media URL from relative path
 */
export const getMediaUrl = (path) => {
  if (!path) return "";
  const fixed = fixFilePath(path);
  // Ensure the base URL is correctly used (e.g., http://localhost:3000)
  return `${API_BASE_URL}/${fixed}`;
};

const ProofCard = ({ proof, onEdit, onDelete }) => {
  console.log(proof);
  const [showGallery, setShowGallery] = useState(false);

  // In ProofCard component

  const getMediaPreview = () => {
    // 1. Initial check for no media (already present, but good)
    if (!proof?.media || proof.media.length === 0) {
      return <Image size={48} className="text-blue-500/40" />;
    }

    const firstMedia = proof.media[0];

    // 2. CRUCIAL: Add a check if firstMedia itself is valid
    if (!firstMedia || !firstMedia.path || !firstMedia.type) {
      return <Image size={48} className="text-blue-500/40" />;
    }

    const mediaUrl = getMediaUrl(firstMedia.path);

    // 3. CORRECTED: Use optional chaining to safely call startsWith (though step 2 fixes it more robustly)
    if (firstMedia.type?.startsWith("image")) {
      return (
        <img
          src={mediaUrl}
          alt="preview"
          className="w-full h-full object-cover"
        />
      );
      // 4. CORRECTED: Use optional chaining
    } else if (firstMedia.type?.startsWith("video")) {
      return (
        <div className="w-full h-full bg-slate-700 flex items-center justify-center relative">
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <Play size={48} className="text-white/70" />
          </div>
        </div>
      );
    }

    return <Image size={48} className="text-blue-500/40" />;
  };

  // In ProofCard component

  const getMediaTypeIcon = () => {
    // 1. Initial check for no media
    if (!proof?.media || proof.media.length === 0) return null;

    const mediaCount = proof.media.length;

    // 🟢 CORRECTED: Use optional chaining (m.type?) to safely call startsWith
    const hasImages = proof.media.some((m) => m.type?.startsWith("image"));
    const hasVideos = proof.media.some((m) => m.type?.startsWith("video"));

    if (hasImages && hasVideos) {
      return (
        <span className="px-2 py-1 bg-blue-600/40 text-blue-300 text-xs rounded font-semibold">
          Mixed ({mediaCount})
        </span>
      );
    } else if (hasVideos) {
      return (
        <span className="px-2 py-1 bg-purple-600/40 text-purple-300 text-xs rounded font-semibold">
          Videos ({mediaCount})
        </span>
      );
    } else if (hasImages) {
      return (
        <span className="px-2 py-1 bg-cyan-600/40 text-cyan-300 text-xs rounded font-semibold">
          Images ({mediaCount})
        </span>
      );
    }
    return null;
  };
  return (
    <div className="group bg-slate-800 rounded-xl border border-slate-700 overflow-hidden hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 cursor-pointer">
      {/* Media Preview - Clickable to open gallery */}
      <div
        onClick={() => setShowGallery(true)}
        className="bg-gradient-to-br from-blue-600/10 to-slate-800 h-32 flex items-center justify-center border-b border-slate-700 relative overflow-hidden hover:from-blue-600/20 transition-colors"
      >
        {getMediaPreview()}
        {/* Badge showing additional files */}
        {proof?.media?.length > 1 && (
          <div className="absolute top-2 right-2 bg-black/60 backdrop-blur px-2 py-1 rounded text-xs text-white font-semibold">
            +{proof.media.length - 1}
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-slate-100 capitalize">
              {proof.category}
            </h3>
            <p className="text-xs text-slate-400 capitalize mt-1">
              {proof.type}
            </p>
          </div>
          <span className="px-2.5 py-1 bg-slate-700 text-slate-300 text-xs rounded-md font-medium">
            {proof.orientation || "—"}
          </span>
        </div>

        {/* Media type indicator */}
        <div className="mb-3">{getMediaTypeIcon()}</div>

        {/* First media's level if it exists */}
        {proof?.media?.[0]?.level && (
          <div className="mb-3 text-xs text-blue-400 font-medium bg-blue-900/20 px-2 py-1 rounded w-fit">
            {proof.media[0].level}
          </div>
        )}

        <p className="text-sm text-slate-400 line-clamp-2 mb-5">
          {proof.description}
        </p>

        <div className="flex gap-2.5">
          <button
            onClick={() => onEdit(proof)}
            className="flex-1 px-3 py-2 bg-slate-700 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 rounded-lg transition-all flex items-center justify-center gap-2 text-sm font-medium border border-transparent hover:border-blue-500/30"
          >
            <Edit2 size={16} />
            Edit
          </button>
          <button
            onClick={() => onDelete(proof._id)}
            className="flex-1 px-3 py-2 bg-slate-700 hover:bg-red-600/20 text-red-400 hover:text-red-300 rounded-lg transition-all flex items-center justify-center gap-2 text-sm font-medium border border-transparent hover:border-red-500/30"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const SocialCard = ({ social, onEdit, onDelete }) => {
  return (
    <div className="group bg-slate-800 rounded-xl border border-slate-700 p-5 hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-slate-100">{social.label}</h3>
          <a
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-400 hover:text-blue-300 truncate flex items-center gap-1.5 mt-2 transition-colors"
          >
            <ExternalLink size={14} />
            Visit
          </a>
        </div>
        <span
          className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
            social.status === "active"
              ? "bg-emerald-900/40 text-emerald-300"
              : "bg-slate-700 text-slate-400"
          }`}
        >
          {social.status}
        </span>
      </div>
      <div className="flex gap-2.5">
        <button
          onClick={() => onEdit(social)}
          className="flex-1 px-3 py-2 bg-slate-700 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 rounded-lg transition-all flex items-center justify-center gap-2 text-sm font-medium border border-transparent hover:border-blue-500/30"
        >
          <Edit2 size={16} />
          Edit
        </button>
        <button
          onClick={() => onDelete(social._id)}
          className="flex-1 px-3 py-2 bg-slate-700 hover:bg-red-600/20 text-red-400 hover:text-red-300 rounded-lg transition-all flex items-center justify-center gap-2 text-sm font-medium border border-transparent hover:border-red-500/30"
        >
          <Trash2 size={16} />
          Delete
        </button>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("proofs");
  const [proofs, setProofs] = useState([]);
  const [socials, setSocials] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [proofModalOpen, setProofModalOpen] = useState(false);
  const [socialModalOpen, setSocialModalOpen] = useState(false);
  const [editingProof, setEditingProof] = useState(null);
  const [editingSocial, setEditingSocial] = useState(null);

  // useEffect(() => {
  //   fetchData();
  // }, []);

  // const fetchData = async () => {
  //   setIsLoading(true);
  //   try {
  //     const [proofsRes, socialsRes] = await Promise.all([
  //       apiCall("/admin/proof"),
  //       apiCall("/admin/social"),
  //     ]);
  //     setProofs(proofsRes.data || []);
  //     setSocials(socialsRes.data || []);
  //   } catch (error) {
  //     showAlert("error", "Failed to fetch data");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [proofsRes, socialsRes] = await Promise.all([
        apiCall("/admin/proof"),
        apiCall("/admin/social"),
      ]);
      setProofs(proofsRes.data || []);
      setSocials(socialsRes.data || []);
    } catch (error) {
      showAlert("error", "Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array since it doesn't depend on any props or state
  
  useEffect(() => {
    fetchData();
  }, [fetchData]); // Now fetchData is included in dependencies

  const showAlert = (type, message) => {
    setAlert({ type, message });
  };

  const handleAddProof = () => {
    setEditingProof(null);
    setProofModalOpen(true);
  };

  const handleEditProof = (proof) => {
    setEditingProof(proof);
    setProofModalOpen(true);
  };

  const handleProofSubmit = async (formData, proofId) => {
    setIsLoading(true);
    try {
      if (proofId) {
        // 🟢 CORRECTED API CALL FOR UPDATE: Use PUT to the specific ID
        await apiCall(`/admin/proof/update/${proofId}`, {
          method: "PUT",
          body: formData, // FormData for file upload
        });
        showAlert("success", "Proof updated successfully");
      } else {
        // 🟢 CORRECTED API CALL FOR CREATE: Use POST to the base endpoint
        await apiCall("/admin/proof/create", {
          method: "POST",
          body: formData, // FormData for file upload
        });
        showAlert("success", "Proof created successfully");
      }
      setProofModalOpen(false);
      fetchData();
    } catch (error) {
      showAlert(
        "error",
        `Failed to save proof: ${error.message || "Unknown error"}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProof = async (id) => {
    if (!window.confirm("Are you sure you want to delete this proof?")) return;
    setIsLoading(true);
    try {
      // Assuming your DELETE route is now RESTful: DELETE /admin/proof/:id
      await apiCall(`/admin/proof/delete/${id}`, { method: "DELETE" });
      showAlert("success", "Proof deleted successfully");
      fetchData();
    } catch (error) {
      showAlert("error", "Failed to delete proof");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSocial = () => {
    setEditingSocial(null);
    setSocialModalOpen(true);
  };

  const handleEditSocial = (social) => {
    setEditingSocial(social);
    setSocialModalOpen(true);
  };

  const handleSocialSubmit = async (formData, socialId) => {
    setIsLoading(true);
    try {
      if (socialId) {
        await apiCall(`/admin/social/update/${socialId}`, {
          // Adjusted to be RESTful
          method: "PATCH",
          body: JSON.stringify(formData),
        });
        showAlert("success", "Social media updated successfully");
      } else {
        await apiCall("/admin/social/create", {
          // Adjusted to be RESTful
          method: "POST",
          body: JSON.stringify(formData),
        });
        showAlert("success", "Social media added successfully");
      }
      setSocialModalOpen(false);
      fetchData();
    } catch (error) {
      showAlert("error", "Failed to save social media");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSocial = async (id) => {
    if (!window.confirm("Are you sure you want to delete this social media?"))
      return;
    setIsLoading(true);
    try {
      await apiCall(`/admin/social/delete/${id}`, { method: "DELETE" }); // Adjusted to be RESTful
      showAlert("success", "Social media deleted successfully");
      fetchData();
    } catch (error) {
      showAlert("error", "Failed to delete social media");
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    window.location.href = "/admin/login";
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-blue-400">Admin Panel</h1>
              <p className="text-sm text-slate-400 mt-1">
                Manage proofs and social media
              </p>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 bg-red-950/40 border border-red-900/50 text-red-400 hover:bg-red-950/60 rounded-lg transition-all font-medium"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-1 mb-8 border-b border-slate-800">
          <button
            onClick={() => setActiveTab("proofs")}
            className={`px-6 py-3 font-semibold border-b-2 transition-all flex items-center gap-2 ${
              activeTab === "proofs"
                ? "border-blue-500 text-blue-400"
                : "border-transparent text-slate-400 hover:text-slate-300"
            }`}
          >
            <Image size={20} />
            Proofs
          </button>
          <button
            onClick={() => setActiveTab("socials")}
            className={`px-6 py-3 font-semibold border-b-2 transition-all flex items-center gap-2 ${
              activeTab === "socials"
                ? "border-blue-500 text-blue-400"
                : "border-transparent text-slate-400 hover:text-slate-300"
            }`}
          >
            <ExternalLink size={20} />
            Social Media
          </button>
        </div>

        {activeTab === "proofs" && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-bold text-slate-100">
                Manage Proofs
              </h2>
              <button
                onClick={handleAddProof}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-medium shadow-lg shadow-blue-600/20"
              >
                <Plus size={20} />
                Add Proof
              </button>
            </div>
            {isLoading && proofs.length === 0 ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-blue-500" size={48} />
              </div>
            ) : proofs.length === 0 ? (
              <div className="bg-slate-800 rounded-xl border border-slate-700 p-16 text-center">
                <Image size={56} className="mx-auto text-slate-600 mb-4" />
                <p className="text-slate-400">
                  No proofs yet. Add one to get started.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {proofs.map((proof) => (
                  <ProofCard
                    key={proof._id}
                    proof={proof}
                    onEdit={handleEditProof}
                    onDelete={handleDeleteProof}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "socials" && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-bold text-slate-100">
                Manage Social Media Links
              </h2>
              <button
                onClick={handleAddSocial}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-medium shadow-lg shadow-blue-600/20"
              >
                <Plus size={20} />
                Add Link
              </button>
            </div>
            {isLoading && socials.length === 0 ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-blue-500" size={48} />
              </div>
            ) : socials.length === 0 ? (
              <div className="bg-slate-800 rounded-xl border border-slate-700 p-16 text-center">
                <ExternalLink
                  size={56}
                  className="mx-auto text-slate-600 mb-4"
                />
                <p className="text-slate-400">
                  No social media links yet. Add one to get started.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {socials.map((social) => (
                  <SocialCard
                    key={social._id}
                    social={social}
                    onEdit={handleEditSocial}
                    onDelete={handleDeleteSocial}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ProofFormModal
        isOpen={proofModalOpen}
        onClose={() => setProofModalOpen(false)}
        proof={editingProof}
        onSubmit={handleProofSubmit}
        isLoading={isLoading}
      />

      <SocialFormModal
        isOpen={socialModalOpen}
        onClose={() => setSocialModalOpen(false)}
        social={editingSocial}
        onSubmit={handleSocialSubmit}
        isLoading={isLoading}
      />

      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
